import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { DraftCard } from "../components/DraftCard";
import { TopicModal } from "../components/TopicModal";
import { EmptyState } from "../components/EmptyState";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Button } from "../components/ui/Button";
import { SkeletonList } from "../components/ui/Skeleton";
import { toast } from "../lib/toast";
import { useDrafts } from "../hooks/useDrafts";
import { useDeletePost } from "../hooks/useDeletePost";
import { useGenerateWithTopic } from "../hooks/useGenerateWithTopic";
import { useDraftPollingAfterGeneration } from "../hooks/useDraftPollingAfterGeneration";
import { api } from "../lib/api";

const phaseLabels: Record<string, string> = {
  idle: "",
  fetching_voice: "Buscando voz...",
  researching: "Pesquisando...",
  writing: "Escrevendo...",
  persisting: "Salvando...",
  done: "Pronto!",
  error: "Erro — tentar de novo",
};

const DraftList = () => {
  const { data, isLoading, error } = useDrafts();
  const navigate = useNavigate();
  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const gen = useGenerateWithTopic();
  const [waitingForGen, setWaitingForGen] = useState(false);
  const deletePost = useDeletePost();
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);
  const baselineCountRef = useState<{ value: number }>({ value: 0 })[0];

  const stopWaiting = useCallback(() => setWaitingForGen(false), []);
  useDraftPollingAfterGeneration({
    waiting: waitingForGen,
    onStop: stopWaiting,
    baselineCount: baselineCountRef.value,
  });

  const generateNow = useMutation({
    mutationFn: () => api.generation.now(),
    onSuccess: () => {
      toast.success("Geração iniciada", {
        description: "Draft aparece em 15-30s (atualização automática).",
      });
      baselineCountRef.value = data?.length ?? 0;
      setWaitingForGen(true);
    },
    onError: (err: Error) => {
      toast.error("Falha ao iniciar geração", { description: err.message });
    },
  });

  useEffect(() => {
    if (gen.phase === "done" && gen.result) {
      const postId = gen.result.postId;
      navigate(`/drafts/${postId}`);
      setTopicModalOpen(false);
      gen.reset();
    }
  }, [gen.phase, gen.result, navigate, gen]);

  const isGenerating = gen.phase !== "idle" && gen.phase !== "done" && gen.phase !== "error";

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Drafts</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Posts gerados, aguardando aprovação.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setTopicModalOpen(true)}>
            Novo com tema
          </Button>
          <Button
            variant="primary"
            onClick={() => generateNow.mutate()}
            loading={generateNow.isPending}
          >
            {generateNow.isPending ? "Disparando..." : "Gerar novo draft"}
          </Button>
        </div>
      </div>

      {isLoading && <SkeletonList count={4} />}
      {error && <div className="text-sm text-red-600">Erro: {(error as Error).message}</div>}

      {!isLoading && data && data.length === 0 && (
        <EmptyState
          title="Nenhum draft pendente"
          message="Próximo draft é gerado terça ou quinta às 8h BR. Ou clique 'Gerar novo draft' acima."
        />
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.map((d) => (
            <DraftCard key={d.id} draft={d} onDelete={setDeleteCandidate} />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteCandidate !== null}
        title="Deletar este draft?"
        description="O draft sai da lista e não poderá ser recuperado."
        confirmLabel="Deletar"
        cancelLabel="Cancelar"
        destructive
        loading={deletePost.isPending}
        onConfirm={() => {
          if (deleteCandidate) {
            deletePost.mutate(
              { id: deleteCandidate, kind: "draft" },
              {
                onSuccess: () => toast.success("Draft deletado"),
                onError: (err: Error) =>
                  toast.error("Falha ao deletar draft", { description: err.message }),
                onSettled: () => setDeleteCandidate(null),
              }
            );
          }
        }}
        onCancel={() => setDeleteCandidate(null)}
      />

      <TopicModal
        isOpen={topicModalOpen}
        onClose={() => {
          setTopicModalOpen(false);
          if (gen.phase === "error") gen.reset();
        }}
        onSubmit={(t) => gen.generate(t)}
        isLoading={isGenerating}
        buttonLabel={phaseLabels[gen.phase]}
      />
    </div>
  );
};

export default DraftList;
